import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { OnChangeFn, SortingState } from "@tanstack/react-table";
import debounce from "lodash.debounce";
import { searchKeys, useExtendedSearchQuery } from "src/api/client/extended-search.api";
import type { IExtendedSearchItem } from "src/api/client/extended-search.api.types";
import {
    SEARCH_TABLE_LARGE_PAGE_DEBOUNCE_MS,
    SEARCH_TABLE_LARGE_PAGE_SIZE_THRESHOLD,
    SEARCH_TABLE_NAME_COLUMN_ID,
} from "src/components/pages/search/components/table/consts";
import { mapStoreSortingToTableSortingState } from "src/components/pages/search/components/table/helpers/map-store-sorting-to-table-sorting-state";
import { useLastKnownRowCount } from "src/components/pages/search/hooks/use-last-known-row-count";
import { useLimitSearchTablePageCache } from "src/components/pages/search/hooks/use-limit-search-table-page-cache";
import { useSearchTablePagination } from "src/components/pages/search/hooks/use-search-table-pagination";
import { ISearchRequestParamsOmitCountry } from "src/components/pages/search/search.types";
import { handleTableSortingChange } from "src/components/pages/search/store/actions";
import { getSearchState, useSearchStore } from "src/components/pages/search/store/search-store";

interface IUseSearchTableResultsParams {
    searchParams: ISearchRequestParamsOmitCountry | null;
    enabled?: boolean;
}

export const useSearchTableResults = ({ searchParams, enabled = true }: IUseSearchTableResultsParams) => {
    const { selectedRanking, sortDirection: storeSortDirection } = useSearchStore();
    const storeSorting = useMemo(
        () => mapStoreSortingToTableSortingState(selectedRanking, storeSortDirection),
        [selectedRanking, storeSortDirection]
    );
    const pendingSortingRef = useRef<SortingState | null>(null);
    const [pendingSortingState, setPendingSortingState] = useState<SortingState | null>(null);
    const [appliedStoreSorting, setAppliedStoreSorting] = useState(storeSorting);
    if (appliedStoreSorting !== storeSorting) {
        setAppliedStoreSorting(storeSorting);
        pendingSortingRef.current = null;
        setPendingSortingState(null);
    }
    const sorting = pendingSortingState ?? storeSorting;
    const { pagination, onPaginationChange } = useSearchTablePagination(searchParams);

    const debouncedHandleTableSortingChange = useMemo(() => debounce(handleTableSortingChange, SEARCH_TABLE_LARGE_PAGE_DEBOUNCE_MS), []);
    useEffect(() => () => debouncedHandleTableSortingChange.cancel(), [debouncedHandleTableSortingChange]);

    const onSortingChange: OnChangeFn<SortingState> = useCallback(
        updater => {
            const currentState = getSearchState();
            const baseline =
                pendingSortingRef.current ?? mapStoreSortingToTableSortingState(currentState.selectedRanking, currentState.sortDirection);
            const next = typeof updater === "function" ? updater(baseline) : updater;

            pendingSortingRef.current = next;
            setPendingSortingState(next);

            const activeSorting = next[0];
            const sortingChange: Parameters<typeof handleTableSortingChange>[0] =
                !activeSorting || activeSorting.id === SEARCH_TABLE_NAME_COLUMN_ID
                    ? { sortByRankingId: null, sortDirection: null }
                    : { sortByRankingId: activeSorting.id, sortDirection: activeSorting.desc ? 1 : 0 };

            if (pagination.pageSize > SEARCH_TABLE_LARGE_PAGE_SIZE_THRESHOLD) {
                debouncedHandleTableSortingChange(sortingChange);
            } else {
                handleTableSortingChange(sortingChange);
            }
        },
        [pagination.pageSize, debouncedHandleTableSortingChange]
    );

    const tableSearchParams = useMemo(() => {
        if (!searchParams) {
            return null;
        }

        return {
            ...searchParams,
            offset: pagination.pageIndex * pagination.pageSize,
            limit: pagination.pageSize,
        };
    }, [pagination.pageIndex, pagination.pageSize, searchParams]);

    const {
        data: response,
        isPending,
        isError,
        dataUpdatedAt,
    } = useExtendedSearchQuery(tableSearchParams ?? {}, searchKeys.tablePage(tableSearchParams), {
        enabled: enabled && tableSearchParams !== null,
    });

    const data: IExtendedSearchItem[] = useMemo(() => response?.results ?? [], [response]);
    const rowCount = useLastKnownRowCount(searchParams, response?.totalResults ?? 0);
    const hasNoData = !isPending && data.length === 0;

    useLimitSearchTablePageCache(pagination.pageSize, dataUpdatedAt);

    return {
        data,
        rowCount,
        isPending,
        isError,
        hasNoData,
        sorting,
        onSortingChange,
        pagination,
        onPaginationChange,
    };
};
